import { useTranslation } from "react-i18next";
import { useCurrency } from "../../common/hooks/useCurrency";
import { memo } from "react";
import { Box } from "../../common/components";
import { createStyles, palette } from "../../styles/theme";
import { Divider, Typography } from "@mui/material";
import { OrderDetail, PreOrderPaymentOptions } from "../../types";
import { useAuth } from "../../common/hooks/useAuth";

const styles = createStyles({
  container: {
    display: "flex",
    flexFlow: "column",
    gap: "8px",
  },
  sumRow: {
    flex: 1,
    display: "flex",
    flexFlow: "row",
    justifyContent: "space-between",
    gap: "8px",
  },
  sumLabel: {
    marginRight: "20px",
    color: palette.grey.primary,
  },
  sumItem: {
    justifyContent: "flex-end",
    color: palette.grey.primary,
  },
});

interface Props {
  orderDetail: OrderDetail;
  showDistributorPrices?: boolean;
}

const OrderSummary = (props: Props) => {
  const { orderDetail, ...rest } = props;
  const showDistributorPrices =
    rest.showDistributorPrices ?? orderDetail.showDistributorPrices ?? false;
  const { t } = useTranslation();
  const { getCurrencyFormatter } = useCurrency();
  const { isAdmin } = useAuth();
  const getCarbonNeutralityFeeTotal = () => {
    if (!orderDetail.pricing.carbonNeutral) {
      return 0;
    }
    return isDistributor && orderDetail.pricing.distributor
      ? (orderDetail.pricing.distributor.carbonNeutralityFeeTotal ?? 0)
      : (orderDetail.pricing.carbonNeutralityFeeTotal ?? 0);
  };

  if (!orderDetail) {
    return null;
  }

  const {
    pricing: { currency },
  } = orderDetail;

  const hidePrice = orderDetail.pricing.quoteRequest && !isAdmin;

  const isDistributor = showDistributorPrices;

  const subtotal =
    isDistributor && orderDetail.pricing.distributor
      ? orderDetail.pricing.distributor.productsTotal
      : orderDetail.pricing.productsTotal;

  const total =
    isDistributor && orderDetail.pricing.distributor
      ? orderDetail.pricing.distributor.orderTotal
      : orderDetail.pricing.orderTotal;

  const shippingTotal =
    isDistributor && orderDetail.pricing.distributor
      ? orderDetail.pricing.distributor.shippingTotal
      : orderDetail.pricing.shippingTotal;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const carbonNeutralityFeeTotal = getCarbonNeutralityFeeTotal();

  const currencyFormatter = getCurrencyFormatter(currency);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.sumRow}>
        <Typography variant="textRegular" sx={styles.sumLabel}>
          {t("order.products")}
        </Typography>
        <Typography variant="textRegular" sx={styles.sumItem}>
          {hidePrice || subtotal.toString() === "TBD"
            ? t("order.tbd")
            : currencyFormatter?.format(subtotal)}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: palette.grey.lightGrey }} />
      {/* <Box sx={styles.sumRow}>
        <Typography variant="textRegular" sx={styles.sumLabel}>
          {t("order.carbon_offset")}
        </Typography>
        <Typography variant="textRegular" sx={styles.sumItem}>
          {hasReachedMaximum
            ? t("order.tbd")
            : currencyFormatter?.format(carbonNeutralityFeeTotal)}
        </Typography>
      </Box> */}
      <Box sx={styles.sumRow}>
        <Typography variant="textRegular" sx={styles.sumLabel}>
          {t("order.subtotal")}
        </Typography>
        <Typography variant="textRegular" sx={styles.sumItem}>
          {hidePrice || subtotal.toString() === "TBD"
            ? t("order.tbd")
            : currencyFormatter?.format(subtotal)}
        </Typography>
      </Box>

      <Box sx={styles.sumRow}>
        <Typography variant="textRegular" sx={styles.sumLabel}>
          {t("order.shipping")}
        </Typography>
        <Typography variant="textRegular" sx={styles.sumItem}>
          {hidePrice || shippingTotal.toString() === "TBD"
            ? t("order.tbd")
            : currencyFormatter?.format(shippingTotal)}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: palette.grey.lightGrey }} />
      {/* {discounts.map((discount) => (
        <Box sx={styles.sumRow} key={discount.id}>
          <Typography variant="textRegular" sx={styles.sumLabel}>
            {t("order.discount")} ({discount.name})
          </Typography>
          <Typography variant="textRegular" sx={styles.sumItem}>
            {currencyFormatter?.format(discount.amount)}
          </Typography>
        </Box>
      ))} */}
      {/* {orderDetail?.pricing.map((tax) => (
        <Box sx={styles.sumRow} key={tax.id}>
          <Typography variant="textRegular" sx={styles.sumLabel}>
            {t("order.tax")} {tax.jurisdiction} {tax.name} ({tax.percentage}%)
          </Typography>
          <Typography variant="textRegular" sx={styles.sumItem}>
            {currencyFormatter?.format(tax.amount)}
          </Typography>
        </Box>
      ))} */}
      <Box sx={styles.sumRow}>
        <Typography variant="textRegular" sx={styles.sumLabel}>
          {t("order.total_excluding_taxes")}
        </Typography>
        <Typography variant="textRegular" sx={styles.sumItem}>
          {hidePrice || total.toString() === "TBD"
            ? t("order.tbd")
            : currencyFormatter?.format(total)}
        </Typography>
      </Box>
      {orderDetail?.paymentOption === PreOrderPaymentOptions.CREDIT_CARD && (
        <>
          <Divider sx={{ borderColor: palette.grey.lightGrey }} />
          <Box sx={styles.sumRow}>
            <Typography variant="textRegular" sx={styles.sumLabel}>
              {t("order.credit_card_fee")}
            </Typography>
            <Typography variant="textRegular" sx={styles.sumItem}>
              {t("order.tbc")}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default memo(OrderSummary);
